<template>
    <AmModal
        :open="open"
        size="medium"
        eyebrow="АККАУНТ"
        title="Настройки"
        body-flush
        @close="$emit('close')"
    >
            <div class="bento-body">
                <nav class="bento-nav" role="tablist">
                    <button
                        v-for="item in navItems"
                        :key="item.id"
                        type="button"
                        role="tab"
                        class="bento-nav-item"
                        :class="{ 'bento-nav-item--active': activeSection === item.id }"
                        @click="activeSection = item.id"
                    >
                        {{ item.label }}
                    </button>
                </nav>

                <div class="bento-main">
                    <!-- Профиль -->
                    <section v-if="activeSection === 'profile'" class="section">
                        <div class="bento-card bento-card--banner profile-hero">
                            <div class="bento-avatar">{{ initials }}</div>
                            <div class="profile-hero-text">
                                <div class="profile-name">{{ displayName }}</div>
                                <div class="bento-muted bento-small">{{ userInfo.email || '—' }}</div>
                            </div>
                        </div>

                        <form class="bento-card profile-form" @submit.prevent="saveProfile">
                            <div class="bento-grid-2">
                                <div class="bento-field">
                                    <label class="bento-label">Имя</label>
                                    <input v-model="profileForm.name" type="text" class="bento-input" />
                                </div>
                                <div class="bento-field">
                                    <label class="bento-label">Фамилия</label>
                                    <input v-model="profileForm.user_lastname" type="text" class="bento-input" />
                                </div>
                            </div>
                            <div class="bento-field">
                                <label class="bento-label">Компания</label>
                                <input v-model="profileForm.company" type="text" class="bento-input" />
                            </div>
                            <div class="bento-field">
                                <label class="bento-label">Должность</label>
                                <input v-model="profileForm.position" type="text" class="bento-input" />
                            </div>
                            <div class="bento-field">
                                <label class="bento-label">Email</label>
                                <input :value="userInfo.email" type="email" class="bento-input" disabled />
                            </div>
                            <p v-if="profileError" class="form-error">{{ profileError }}</p>
                            <p v-if="profileSaved" class="form-success">Сохранено</p>
                            <AmButton type="submit" variant="dark" :disabled="profileSaving">
                                {{ profileSaving ? 'Сохранение…' : 'Сохранить' }}
                            </AmButton>
                        </form>
                    </section>

                    <!-- Подписка -->
                    <section v-else-if="activeSection === 'subscription'" class="section">
                        <ChatSkeleton v-if="planLoading" variant="card" :count="3" />
                        <template v-else>
                            <div class="bento-card bento-card--banner plan-banner">
                                <div class="plan-big-name">{{ planData?.plan?.name || 'Free' }}</div>
                                <div class="bento-muted bento-small">код: {{ planData?.plan?.code || 'free' }}</div>
                                <p v-if="planData?.expires_at" class="bento-small" style="margin-top: 10px">
                                    Действует до {{ formatDate(planData.expires_at) }}
                                </p>
                            </div>
                            <div class="bento-grid-2">
                                <div class="bento-card bento-stat">
                                    <div class="bento-stat-value">{{ planData?.plan?.requests_per_day ?? '—' }}</div>
                                    <div class="bento-stat-label">запросов / день</div>
                                </div>
                                <div class="bento-card bento-stat">
                                    <div class="bento-stat-value">{{ formatNum(planData?.plan?.tokens_per_month) }}</div>
                                    <div class="bento-stat-label">токенов / месяц</div>
                                </div>
                            </div>
                            <div v-if="addonSources.length" class="bento-card">
                                <div class="bento-label" style="margin-bottom: 10px">Доп. источники</div>
                                <ul class="addon-list">
                                    <li v-for="src in addonSources" :key="src.code">
                                        {{ src.name }}
                                        <span v-if="src.expires_at" class="bento-muted bento-small">
                                            · до {{ formatDate(src.expires_at) }}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                            <AmButton variant="primary" block @click="$emit('upgrade')">
                                Выбрать тариф
                            </AmButton>
                        </template>
                    </section>

                    <!-- Использование -->
                    <section v-else-if="activeSection === 'usage'" class="section">
                        <div class="bento-grid-2">
                            <div class="bento-card">
                                <div class="bento-stat-value">{{ requestsUsed }}</div>
                                <div class="bento-stat-label">запросов использовано</div>
                                <div class="bento-progress">
                                    <div class="bento-progress-fill" :style="{ width: requestsPercent + '%' }" />
                                </div>
                                <div class="bento-muted bento-small" style="margin-top: 8px">из {{ requestsTotal }}</div>
                            </div>
                            <div class="bento-card">
                                <div class="bento-stat-value">{{ formatNum(tokensUsed) }}</div>
                                <div class="bento-stat-label">токенов использовано</div>
                                <div class="bento-progress">
                                    <div class="bento-progress-fill" :style="{ width: tokensPercent + '%' }" />
                                </div>
                                <div class="bento-muted bento-small" style="margin-top: 8px">из {{ formatNum(tokensTotal) }}</div>
                            </div>
                        </div>
                    </section>

                    <!-- Оплаты -->
                    <section v-else-if="activeSection === 'payments'" class="section">
                        <div class="quick-nav">
                            <button type="button" class="bento-btn bento-btn--ghost" @click="activeSection = 'subscription'">
                                Подписка →
                            </button>
                            <button type="button" class="bento-btn bento-btn--ghost" @click="$emit('upgrade')">
                                Тарифы →
                            </button>
                            <button type="button" class="bento-btn bento-btn--ghost" @click="$emit('open-tickets', 'billing')">
                                Проблема с оплатой?
                            </button>
                        </div>

                        <div class="bento-card payments-card">
                            <div class="payments-head">
                                <span class="payments-title">История оплат</span>
                                <button
                                    v-if="orders.length > previewLimit"
                                    type="button"
                                    class="bento-btn bento-btn--ghost bento-btn--sm"
                                    @click="showAllOrders = !showAllOrders"
                                >
                                    {{ showAllOrders ? 'Свернуть' : `Все (${orders.length})` }}
                                </button>
                            </div>
                            <ChatSkeleton v-if="ordersLoading" variant="table" :count="5" />
                            <div v-else-if="displayedOrders.length === 0" class="state-empty">Покупок пока нет</div>
                            <table v-else class="payments-table">
                                <thead>
                                    <tr>
                                        <th>Название</th>
                                        <th>Сумма</th>
                                        <th>Статус</th>
                                        <th>Дата</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="order in displayedOrders" :key="order.id">
                                        <td>
                                            <div class="order-title-cell">{{ order.title }}</div>
                                            <div class="order-type-cell">{{ formatProductType(order.product_type) }}</div>
                                        </td>
                                        <td>{{ formatOrderPrice(order.amount_cents) }}</td>
                                        <td>
                                            <span class="bento-pill" :class="order.status === 'paid' ? 'bento-pill--paid' : 'bento-pill--pending'">
                                                {{ formatOrderStatus(order.status) }}
                                            </span>
                                        </td>
                                        <td class="date-cell">{{ formatDate(order.created_at) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
    </AmModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import AmButton from '@/components/ui/AmButton.vue'
import AmModal from '@/components/ui/AmModal.vue'
import ChatSkeleton from '@/components/chat/ChatSkeleton.vue'
import { getChatPlan } from '@/utils/chatApi'
import {
    fetchPaymentOrders,
    formatOrderPrice,
    formatOrderStatus,
    formatProductType,
} from '@/utils/paymentsApi'
import { updateUser } from '@/utils/requests'

const props = defineProps({
    open: { type: Boolean, default: false },
    initialSection: { type: String, default: 'profile' },
    userInfo: { type: Object, default: () => ({}) },
    tokensUsed: { type: Number, default: 0 },
    tokensTotal: { type: Number, default: 0 },
    requestsUsed: { type: Number, default: 0 },
    requestsTotal: { type: Number, default: 0 },
})

const emit = defineEmits(['close', 'upgrade', 'open-tickets', 'profile-saved'])

const store = useStore()

const navItems = [
    { id: 'profile', label: 'Профиль' },
    { id: 'subscription', label: 'Подписка' },
    { id: 'usage', label: 'Использование' },
    { id: 'payments', label: 'Оплаты' },
]

const activeSection = ref('profile')
const showAllOrders = ref(false)
const previewLimit = 6

const profileForm = ref({ name: '', user_lastname: '', company: '', position: '' })
const profileSaving = ref(false)
const profileError = ref('')
const profileSaved = ref(false)

const planLoading = ref(false)
const planData = ref(null)
const ordersLoading = ref(false)
const orders = ref([])

const initials = computed(() => {
    const source = displayName.value || ''
    const parts = source.split(' ').filter(Boolean)
    if (parts.length === 0) return 'AM'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
})

const displayName = computed(() => {
    const u = props.userInfo
    if (u?.name) return [u.name, u.user_lastname].filter(Boolean).join(' ')
    return 'Аналитик'
})

const addonSources = computed(() => planData.value?.addon_sources || [])

const requestsPercent = computed(() => {
    if (!props.requestsTotal) return 0
    return Math.min(100, Math.round((props.requestsUsed / props.requestsTotal) * 100))
})

const tokensPercent = computed(() => {
    if (!props.tokensTotal) return 0
    return Math.min(100, Math.round((props.tokensUsed / props.tokensTotal) * 100))
})

const displayedOrders = computed(() => {
    if (showAllOrders.value) return orders.value
    return orders.value.slice(0, previewLimit)
})

function syncProfileForm() {
    const u = props.userInfo
    profileForm.value = {
        name: u?.name || '',
        user_lastname: u?.user_lastname || '',
        company: u?.company || '',
        position: u?.position || '',
    }
}

async function saveProfile() {
    const userId = props.userInfo?.id
    if (!userId) return
    profileSaving.value = true
    profileError.value = ''
    profileSaved.value = false
    try {
        const response = await updateUser(userId, {
            name: profileForm.value.name,
            user_lastname: profileForm.value.user_lastname,
            company: profileForm.value.company,
            position: profileForm.value.position,
        })
        if (!response.ok) {
            throw new Error('Не удалось сохранить')
        }
        await store.dispatch('main/refreshCurrentUser')
        const updated = store.getters['main/userInfo'] || { ...props.userInfo, ...profileForm.value }
        store.commit('main/setUserInfo', updated)
        emit('profile-saved', updated)
        profileSaved.value = true
        setTimeout(() => { profileSaved.value = false }, 2500)
    } catch (e) {
        profileError.value = e?.message || 'Ошибка сохранения'
    } finally {
        profileSaving.value = false
    }
}

function formatDate(iso) {
    if (!iso) return '—'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatNum(n) {
    if (n == null) return '—'
    return Number(n).toLocaleString('ru-RU')
}

async function loadPlan() {
    planLoading.value = true
    try {
        planData.value = await getChatPlan()
    } catch {
        planData.value = null
    } finally {
        planLoading.value = false
    }
}

async function loadOrders() {
    ordersLoading.value = true
    try {
        const { items } = await fetchPaymentOrders({ per_page: 100 })
        orders.value = items
    } catch {
        orders.value = []
    } finally {
        ordersLoading.value = false
    }
}

watch(
    () => props.open,
    (isOpen) => {
        if (!isOpen) return
        activeSection.value = props.initialSection || 'profile'
        showAllOrders.value = props.initialSection === 'payments'
        syncProfileForm()
        loadPlan()
        loadOrders()
    },
)

watch(() => props.userInfo, syncProfileForm, { deep: true })
</script>

<style scoped>
.section {
    max-width: 100%;
}

.profile-hero {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 16px;
}

.profile-hero-text {
    flex: 1;
}

.profile-name {
    font-size: 16px;
    font-weight: 600;
    color: #23282C;
}

.profile-form {
    margin-top: 0;
}

.plan-big-name {
    font-size: 20px;
    font-weight: 600;
}

.addon-list {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.6;
}

.quick-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
}

.payments-card {
    padding: 0;
    overflow: hidden;
    background: white;
}

.payments-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid #e0e0e0;
}

.payments-title {
    font-size: 13px;
    font-weight: 600;
    color: #23282C;
}

.payments-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
}

.payments-table th {
    text-align: left;
    padding: 10px 18px;
    color: #888888;
    font-weight: 500;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.payments-table td {
    padding: 12px 18px;
    border-top: 1px solid #e7e7e7;
    color: #23282C;
}

.order-title-cell {
    font-weight: 500;
}

.order-type-cell {
    font-size: 11px;
    color: #888888;
    margin-top: 2px;
}

.date-cell {
    color: #888888;
    white-space: nowrap;
}

.state-empty {
    padding: 24px;
    text-align: center;
    font-size: 13px;
    color: #888888;
}

.form-error {
    color: #E30613;
    font-size: 12px;
    margin: 0 0 8px;
}

.form-success {
    color: #5f5f5f;
    font-size: 12px;
    margin: 0 0 8px;
}

</style>
